import i18n, { type KeyPrefix } from "i18next";
import {
  UseTranslationOptions,
  UseTranslationResponse,
  initReactI18next,
} from "react-i18next";
import {
  type Localisation,
  type Namespace,
  english,
} from "../localisations/en";
import { englishUS } from "../localisations/enUS";
import { englishCA } from "../localisations/enCA";
import { spanish } from "../localisations/es";
import { french } from "../localisations/fr";
import { dutch } from "../localisations/nl";
import { italian } from "../localisations/it";
import { first } from "lodash";
import { config } from "../config";
import { englishDevelopment } from "../localisations/enDev";
import type { Locale } from "date-fns";
import {
  de,
  enCA,
  enGB,
  enUS,
  es,
  fr,
  it,
  nl,
  pt,
  ptBR,
} from "date-fns/locale";
import { portuguese } from "../localisations/pt";
import { portugueseBrazilian } from "../localisations/ptBR";
import { german } from "../localisations/de";

export type { Localisation } from "../localisations/en";

const resources = {
  en: config.isDevelopment ? englishDevelopment : english,
  "en-GB": config.isDevelopment ? englishDevelopment : english,
  "en-US": englishUS,
  "en-CA": englishCA,
  es: spanish,
  "es-ES": spanish,
  fr: french,
  nl: dutch,
  it: italian,
  pt: portuguese,
  "pt-BR": portugueseBrazilian,
  de: german,
};

export const dateFnsLocales: { [localeName in LocaleName]: Locale } = {
  de: de,
  en: enGB,
  "en-US": enUS,
  "en-GB": enGB,
  "en-CA": enCA,
  es: es,
  "es-ES": es,
  fr: fr,
  nl: nl,
  it: it,
  pt: pt,
  "pt-BR": ptBR,
};

export type LocaleName = keyof typeof resources;

const supportedLocales = new Set(Object.keys(resources));
const getLanguageCode = (locale: LocaleName) => first(locale.split("-"));

export const deviceLocale: LocaleName = (() => {
  const locales =
    navigator?.languages ??
    (navigator?.language != null ? [navigator.language] : []);

  for (const locale of locales) {
    if (supportedLocales.has(locale)) {
      return locale as LocaleName;
    } else {
      const language = getLanguageCode(locale as LocaleName);
      if (language != null && supportedLocales.has(language)) {
        return language as LocaleName;
      }
    }
  }

  return "en";
})();

// Augment the return type of react-i18next so that i18n.language returns a `LocaleName` rather than `string`
declare module "react-i18next" {
  type NamespaceOptions<N extends Namespace> = Localisation[N];
  export function useTranslation<
    Ns extends Namespace,
    KPrefix extends KeyPrefix<Ns> = undefined,
  >(
    ns: Ns,
    options?: UseTranslationOptions<KPrefix>,
  ): Omit<UseTranslationResponse<Ns, KPrefix>, "t"> & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    t: (key: keyof NamespaceOptions<Ns>, args?: any) => string;
  };
}

export const initLocalisation = (locale?: LocaleName): void => {
  i18n.use(initReactI18next).init({
    compatibilityJSON: "v4",
    debug: config.isDevelopment,
    resources,
    lng: locale ?? deviceLocale,
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
  });
};
